import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const statusLabel = {
  pending_payment: "Awaiting payment",
  paid: "Paid",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  abandoned: "Abandoned",
};

export default function OrderSlip() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        setOrder(data);
      } catch {
        setErr("Order not found or unavailable.");
      }
    })();
  }, [orderId]);

  useEffect(() => {
    if (order) document.title = `BBK · Slip · ${order.order_number}`;
    return () => { document.title = "Bulk Buy Kits"; };
  }, [order]);

  if (err) return <div className="p-12 overline">{err}</div>;
  if (!order) return <div className="p-12 overline">Loading…</div>;

  const addr = order.address || {};
  const printedAt = new Date().toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <>
      {/* Print-only stylesheet */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .slip-page { box-shadow: none !important; border: 0 !important; padding: 0 !important; margin: 0 !important; }
          .slip-wrap { padding: 0 !important; }
          @page { margin: 20mm 16mm; }
        }
        /* iter142 — user wants all packing-slip text solid black.
           Scoped to .slip-page so we don't tint overlines elsewhere. */
        .slip-page, .slip-page * { color: #000000 !important; }
        .slip-page .overline { color: #000000 !important; }
      `}</style>

      <div className="slip-wrap max-w-[820px] mx-auto px-6 lg:px-12 py-10 lg:py-14" data-testid={`order-slip-${order.order_number}`}>
        <div className="no-print flex items-center justify-between mb-8">
          <Link to="/orders" className="text-[13px] uppercase tracking-[0.22em] inline-flex items-center gap-2 text-black hover:text-charcoal">
            <ArrowLeft size={12} strokeWidth={1.5}/> Back to orders
          </Link>
          <div className="flex items-center gap-2">
            <a
              href={`${BACKEND}/api/orders/${order.id}/slip.pdf`}
              className="inline-flex items-center gap-2 px-5 py-3 border border-borderSubtle hover:border-oxblood text-charcoal hover:text-black tracking-[0.22em] text-xs uppercase transition-colors rounded-full"
              data-testid="pdf-slip-btn"
              target="_blank"
              rel="noreferrer"
            >
              <Download size={14} strokeWidth={2}/> Save as PDF
            </a>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 px-5 py-3 bg-charcoal text-bone tracking-[0.22em] text-xs uppercase hover:bg-oxblood transition-colors rounded-full"
              data-testid="print-slip-btn"
            >
              <Printer size={14} strokeWidth={2}/> Print slip
            </button>
          </div>
        </div>

        <div className="slip-page bg-white border border-borderSubtle p-10">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-charcoal pb-6 mb-8">
            <div>
              <p className="overline text-black mb-2">Bulk Buy Kits</p>
              <h1 className="font-serif text-4xl leading-none">Packing slip.</h1>
              <p className="text-xs text-black mt-2 font-mono">bulkbuykits.com · support@bulkbuykits.com</p>
            </div>
            <div className="text-right">
              <p className="overline">Order</p>
              <p className="font-mono text-2xl text-black">#{order.order_number}</p>
              <p className="text-[12px] font-mono uppercase tracking-widest text-black mt-1">
                {new Date(order.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              </p>
            </div>
          </div>

          {/* Two-column: ship to + payment */}
          <div className="grid md:grid-cols-2 gap-8 mb-8 text-sm">
            <div>
              <p className="overline mb-3">Ship to</p>
              <p className="font-medium">{order.customer_name}</p>
              <p className="text-black">{order.customer_email}</p>
              <p className="mt-2">
                {addr.line1}
                {addr.line2 ? <><br/>{addr.line2}</> : null}
                <br/>
                {addr.city}{addr.city && addr.state ? ", " : ""}{addr.state} {addr.postal_code}
                <br/>
                {addr.country}
              </p>
            </div>
            <div>
              <p className="overline mb-3">Order</p>
              <p><span className="text-black">Status:</span> <span className="overline text-black">{statusLabel[order.status] || order.status}</span></p>
              <p><span className="text-black">Payment:</span> {order.payment_method?.toUpperCase()}</p>
              {order.discount_code && (
                <p><span className="text-black">Code:</span> <span className="font-mono">{order.discount_code}</span></p>
              )}
            </div>
          </div>

          {/* Note to buyer */}
          {order.note_to_buyer && (
            <div className="mb-8 border-l-[3px] border-oxblood bg-[#FAFAFA] px-5 py-4" data-testid="slip-note-to-buyer">
              <p className="overline text-black mb-1">A note from Bulk Buy Kits</p>
              <p className="text-sm text-charcoal whitespace-pre-wrap leading-relaxed">{order.note_to_buyer}</p>
            </div>
          )}

          {/* Items table */}
          <table className="w-full text-sm mb-8 border-t border-b border-charcoal" data-testid="slip-items-table">
            <thead>
              <tr className="text-left border-b border-borderSubtle">
                <th className="overline px-2 py-3">Kit</th>
                <th className="overline px-2 py-3 text-right">Qty</th>
                <th className="overline px-2 py-3 text-right">Unit</th>
                <th className="overline px-2 py-3 text-right">Line</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((i, idx) => {
                // Build the display name once — normalize legacy items whose
                // unit_label duplicated the mg prefix ("10mg · 5 Vials").
                const rawName = String(i.name || "").trim();
                const mgRaw = String(i.variant_mg || "").trim();
                const mgLabel = mgRaw ? (mgRaw.toLowerCase().endsWith("mg") ? mgRaw : `${mgRaw}mg`) : "";
                let unitLabel = String(i.unit_label || "").trim();
                if (mgLabel && unitLabel) {
                  for (const cand of [`${mgLabel} · `, `${mgLabel}· `, `${mgLabel} `]) {
                    if (unitLabel.toLowerCase().startsWith(cand.toLowerCase())) {
                      unitLabel = unitLabel.slice(cand.length).trim();
                      break;
                    }
                  }
                }
                const kitParts = [rawName];
                if (mgLabel) kitParts.push(mgLabel);
                if (unitLabel && unitLabel.toLowerCase() !== mgLabel.toLowerCase()) kitParts.push(unitLabel);
                const kitDisplay = kitParts.filter(Boolean).join(" · ");

                const retailUnit = Number(i.retail_line_price ?? 0);
                const savingsUnit = Number(i.kit_savings ?? 0);
                const singleVial = Number(i.single_vial_price ?? 0);
                const vialCount = Number(i.vial_count ?? 0);
                const showRetail = savingsUnit > 0 && vialCount > 1 && singleVial > 0;
                return (
                  <tr key={idx} className="border-b border-borderSubtle last:border-0">
                    <td className="px-2 py-3">
                      <div>{kitDisplay}</div>
                      {showRetail && (
                        <div className="text-[12px] text-black mt-1 font-mono" data-testid={`slip-line-retail-${idx}`}>
                          ${singleVial.toFixed(2)} single vial × {vialCount} vials = ${retailUnit.toFixed(2)} retail
                          <span className="text-black ml-1"> · saved ${savingsUnit.toFixed(2)}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3 text-right font-mono align-top">{i.quantity}</td>
                    <td className="px-2 py-3 text-right font-mono align-top">${i.price.toFixed(2)}</td>
                    <td className="px-2 py-3 text-right font-mono align-top">${(i.price * i.quantity).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Totals — retail / kit savings / discount code / shipping / total */}
          {(() => {
            const totalRetail = order.items.reduce((s, i) => s + Number(i.retail_line_price ?? i.price ?? 0) * Number(i.quantity ?? 1), 0);
            const totalKitSavings = order.items.reduce((s, i) => s + Number(i.kit_savings ?? 0) * Number(i.quantity ?? 1), 0);
            const codeDiscount = Number(order.discount ?? 0);
            const hasKitSavings = totalKitSavings > 0.005;
            const hasCodeDiscount = codeDiscount > 0.005;
            return (
              <div className="flex justify-end">
                <div className="w-full max-w-xs space-y-1 text-sm">
                  <div className="flex justify-between" data-testid="slip-total-retail">
                    <span className="text-black">Retail</span>
                    <span className="font-mono">${totalRetail.toFixed(2)}</span>
                  </div>
                  {hasKitSavings && (
                    <div className="flex justify-between text-black" data-testid="slip-total-kit-savings">
                      <span>Multi-vial kit savings</span>
                      <span className="font-mono">−${totalKitSavings.toFixed(2)}</span>
                    </div>
                  )}
                  {hasCodeDiscount && (
                    <div className="flex justify-between text-black" data-testid="slip-total-code-discount">
                      <span>Discount code{order.discount_code ? ` (${order.discount_code})` : ""}</span>
                      <span className="font-mono">−${codeDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-black">Shipping</span>
                    <span className="font-mono">{Number(order.shipping || 0) <= 0 ? "FREE" : `$${Number(order.shipping).toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between border-t border-charcoal pt-2 mt-2 font-medium text-base">
                    <span>Total</span><span className="font-mono text-black">${order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="mt-10 pt-6 border-t border-borderSubtle flex items-start gap-6">
            <img
              src={`${BACKEND}/api/orders/${order.id}/slip-qr.png`}
              alt="Scan to view order"
              className="w-24 h-24 shrink-0 border border-borderSubtle bg-white p-1"
              data-testid="slip-qr"
            />
            <div className="text-[13px] text-black flex-1">
              <p className="uppercase tracking-widest text-charcoal font-mono mb-1">Scan to track · status</p>
              <p><span className="text-charcoal font-medium">Research use only.</span> Products supplied by Bulk Buy Kits are strictly for laboratory research and in-vitro study. Not for human or animal consumption.</p>
              <p className="mt-2 font-mono">Printed {printedAt}</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
